/* eslint-disable no-console */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { useChatEventBus } from '@chatbot/composables/useChatEventBus';
import { type ChatApiOptions, type ChatOptions, type Message, type UseChatOptions, type UseChatReturn } from '@chatbot/typings/types';
import { loadPreviousSession, sendMessage } from '@chatbot/utils/message';
import { parseApiMessages } from '@chatbot/utils/message-parser';
import { getConversationId, removeConversationId, saveConversationId } from '@chatbot/utils/storage';

export const useChat = (options: UseChatOptions): UseChatReturn => {
    // Use i18n for translations
    const { t } = useI18n();

    // Inject reactive chat configuration
    const injectedChatOptions = inject<ChatOptions>('chatOptions');

    const {
        initialMessages = [],
        onMessageSent,
        onResponseReceived,
    } = options;

    // Use reactive computed values that update when injected options change
    const n8nWebhookUrl = computed<string>(() => injectedChatOptions?.n8nWebhookChatUrl ?? options.n8nWebhookUrl ?? '');
    const metadata = computed<Record<string, unknown>>(() => {
        // Determine agent based on allStarsQuoteEnabled from injected options
        const agent = injectedChatOptions?.agent ?? 'STARLIGHT';
        const environment = injectedChatOptions?.environment ?? 'development';
        return {
            ...options.metadata,
            ...(injectedChatOptions?.metadata ?? {}),
            venueId: injectedChatOptions?.venueId ?? options.metadata?.venueId,
            venueSettingsUrl: injectedChatOptions?.venueSettingsUrl ?? options.metadata?.venueSettingsUrl,
            agent,
            environment,
        };
    });
    const welcomeMessage = computed<string>(() => injectedChatOptions?.welcomeMessage
            || options.welcomeMessage || t('chat.welcomeMessage'));

    // Helper function to create a welcome message
    // Use a very old date to ensure welcome message always sorts first
    const createWelcomeMessage = (): Message => ({
        id: 'welcome-message',
        conversationId: '',
        role: 'assistant',
        content: welcomeMessage.value,
        createdAt: new Date(0),
    });

    // Initialize session ID from localStorage (don't generate yet)
    const initialSessionId = getConversationId();
    const sessionId = ref<string | null>(initialSessionId);

    // Get event bus instance
    const eventBus = useChatEventBus();

    // State
    const messages = ref<Message[]>([]);
    const isThinking = ref<boolean>(false);
    const isLoadingPreviousConversation = ref<boolean>(false);

    // Watch for welcome message changes and update the first message if it's the welcome message
    watch(welcomeMessage, newWelcome => {
        if (messages.value.length > 0 && messages.value[0].id === 'welcome-message') {
            // Update the content property directly to ensure reactivity
            messages.value[0].content = newWelcome;
        }
    });

    // Start a new conversation
    const newConversation = (): void => {
        // Clear session ID - will be generated on first message
        sessionId.value = null;
        removeConversationId();

        // Reset messages with welcome message
        messages.value = [createWelcomeMessage()];

        // Emit scroll event after resetting conversation
        eventBus.emit('scroll-to-bottom');
    };

    // Load previous conversation if conversation ID exists
    const loadPreviousConversation = async(): Promise<void> => {
        const existingConversationId = getConversationId();

        // If no session ID exists and no initial messages, show welcome message
        if (!existingConversationId) {
            if (initialMessages.length === 0) {
                messages.value = [createWelcomeMessage()];
                eventBus.emit('scroll-to-bottom');
            }
            return;
        }

        isLoadingPreviousConversation.value = true;

        try {
            const chatOptions: ChatApiOptions = {
                webhookUrl: n8nWebhookUrl.value,
                metadata: metadata.value,
            };

            const response = await loadPreviousSession(existingConversationId, chatOptions);

            // Parse message format from API (sorted by timestamp ascending in parseApiMessages)
            if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const parsedMessages = parseApiMessages(response.data);

                // Ensure welcome message is always first (oldest message)
                const hasWelcomeMessage = parsedMessages[0]?.id === 'welcome-message';
                if (hasWelcomeMessage) {
                    messages.value = parsedMessages;
                } else {
                    messages.value = [createWelcomeMessage(), ...parsedMessages];
                }
            }
        } catch (error) {
            console.error('Error loading previous conversation:', error);

            // If loading fails, start with a fresh conversation
            newConversation();
        } finally {
            isLoadingPreviousConversation.value = false;

            // Emit scroll event after loading is complete and DOM has updated
            await nextTick();
            eventBus.emit('scroll-to-bottom');
        }
    };

    // Handle n8n message sending
    const handleN8nMessage = async(text: string): Promise<void> => {
        try {
            const chatOptions: ChatApiOptions = {
                webhookUrl: n8nWebhookUrl.value,
                metadata: metadata.value,
            };

            // Send message with current sessionId (null for first message)
            const response = await sendMessage(
                text, [], sessionId.value ?? '', chatOptions,
            );

            // Extract and save sessionId from response
            if (response.sessionId) {
                // eslint-disable-next-line require-atomic-updates
                sessionId.value = response.sessionId;
                saveConversationId(response.sessionId);
            }

            isThinking.value = false;

            // Emit scroll event when thinking stops
            eventBus.emit('scroll-to-bottom');

            const botMessage: Message = {
                id: (Date.now() + 1).toString(),
                conversationId: sessionId.value ?? '',
                role: 'assistant',
                content: response.output ?? t('chat.noResponseFromServer'),
                createdAt: new Date(),
            };
            messages.value.push(botMessage);

            // Emit scroll event after adding bot message
            eventBus.emit('scroll-to-bottom');

            // Call optional callback
            if (onResponseReceived) {
                onResponseReceived(botMessage);
            }
        } catch (error) {
            console.error('Error sending message to n8n:', error);
            isThinking.value = false;

            // Add error message
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                conversationId: sessionId.value ?? '',
                role: 'assistant',
                content: t('chat.errorProcessingMessage'),
                createdAt: new Date(),
            };
            messages.value.push(errorMessage);

            // Emit scroll event
            eventBus.emit('scroll-to-bottom');
        }
    };

    // Send a message
    const sendMessageToChat = async(text: string): Promise<void> => {
        // Add user message
        const userMessage: Message = {
            id: Date.now()
                .toString(),
            conversationId: sessionId.value ?? '',
            role: 'user',
            content: text,
            createdAt: new Date(),
        };

        messages.value.push(userMessage);

        // Emit scroll event after adding message
        eventBus.emit('scroll-to-bottom');

        // Call optional callback
        if (onMessageSent) {
            onMessageSent(userMessage);
        }

        // Show thinking indicator
        isThinking.value = true;

        // Emit scroll event when thinking starts
        eventBus.emit('scroll-to-bottom');

        // Send message to n8n
        await handleN8nMessage(text);
    };

    /**
     * Send a pending message, ensuring it's sent after any loading operations
     * @param message - The message text to send
     */
    const sendPendingMessage = async(message: string): Promise<void> => {
        // If currently loading previous conversation, wait for it to complete
        if (isLoadingPreviousConversation.value) {
            return new Promise<void>(resolve => {
                const unwatch = watch(isLoadingPreviousConversation, async loading => {
                    if (!loading) {
                        unwatch(); // Stop watching
                        await sendMessageToChat(message);
                        resolve();
                    }
                });
            });
        }

        // If not loading, send immediately
        return sendMessageToChat(message);
    };

    // Initialize messages with initialMessages or load previous conversation
    onMounted(() => {
        if (initialMessages.length > 0) {
            messages.value = [...initialMessages];
        } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            loadPreviousConversation();
        }
    });

    return {
        messages,
        isThinking,
        isLoadingPreviousConversation,
        sendMessage: sendMessageToChat,
        newConversation,
        sendPendingMessage,
    };
};
